import { put, select } from "redux-saga/effects";

import { selectAiApiStatuses } from "store/slices/ai/selectors";

import { setAiChanges, setApiStatus } from "../slice";
import type { processAiActions } from "../slice";
import type { ClarkProcessorResult } from "../types";

/**
 * Dashboard AI websocket batches actions here. The legacy ClarkProcessor that
 * turned those actions into canvas/API edits is removed; the modern app handles
 * AI edits elsewhere. We keep API status bookkeeping for in-flight scaffold
 * requests and clear AI change previews with an empty payload (same as the old
 * no-op processor path).
 */
export function* processAiActionsSaga(
  action: ReturnType<typeof processAiActions>,
) {
  const previousApiStatuses: Record<string, "STARTED" | "COMPLETED"> = yield (
    select(selectAiApiStatuses) || {}
  );

  for (const act of action.payload.actions) {
    if (
      act.action === "scaffoldApi" ||
      act.action === "editApi" ||
      act.action === "createApi"
    ) {
      const previousStatus = previousApiStatuses?.[act.apiName];
      if (!previousStatus) {
        yield put(setApiStatus({ apiName: act.apiName, status: "STARTED" }));
      }
    }
  }

  const processorResult: ClarkProcessorResult = {
    widgetChangedKeys: {},
    widgetChanges: {},
    renamesById: {},
    discardedEdits: {},
    metadataByWidgetId: {},
    apiChanges: {},
    changedApiBlockPaths: {},
  };

  const {
    widgetChangedKeys,
    renamesById,
    widgetChanges,
    metadataByWidgetId,
    discardedEdits,
    apiChanges,
    changedApiBlockPaths,
    stateVariableChanges,
    timerChanges,
    eventChanges,
    stateVariableChangedKeys,
    timerChangedKeys,
    eventChangedKeys,
    widgetMovesById,
    originalNamesById,
  } = processorResult;

  yield put(
    setAiChanges({
      widgetChangedKeys,
      widgetChanges,
      renamesById,
      discardedEdits,
      metadataByWidgetId,
      apiChanges,
      changedApiBlockPaths,
      apiStatuses: undefined,
      stateVariableChanges,
      timerChanges,
      eventChanges,
      stateVariableChangedKeys,
      timerChangedKeys,
      eventChangedKeys,
      widgetMovesById,
      originalNamesById,
    }),
  );

  for (const act of action.payload.actions) {
    if (act.action === "completeApi") {
      const previousStatus = previousApiStatuses?.[act.apiName];
      if (previousStatus !== "COMPLETED") {
        yield put(setApiStatus({ apiName: act.apiName, status: "COMPLETED" }));
      }
    }
  }
}
